import { useTranslation } from '@thoughtspot/i18n';
import { Accordion, AccordionVariant, } from '@thoughtspot/radiant-react/widgets/accordion';
import { Vertical } from '@thoughtspot/radiant-react/widgets/layout';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { SearchBar } from '@thoughtspot/radiant-react/widgets/search-bar';
import { Toggle } from '@thoughtspot/radiant-react/widgets/toggle';
import { Colors, Typography, } from '@thoughtspot/radiant-react/widgets/typography';
import React, { useEffect, useMemo, useState } from 'react';
import { ConfigPrePostFixStrings } from '../../../settings/common';
import { ConfigCard, InputType } from '../../config-components';
import { OrgManagementTranslationKeys } from '../../constants';
import styles from './org-create-wizard.module.scss';
const DATA_MODELLING_CATEGORY = 'dataModelling';
const DATA_MODELLING_FEATURE_TITLE_KEY = 'isTmlUtilityV2Enabled.title';
const noop = () => { };
export const StepFeatureConfig = ({ features, loading, searchTerm, onSearchChange = noop, onFeatureValueChange = noop, readOnly = false, }) => {
    const { t } = useTranslation();
    const [showSelected, setShowSelected] = useState(false);
    const selectedCount = useMemo(() => features.filter(feature => feature.value === 'true').length, [features]);
    useEffect(() => {
        if (selectedCount === 0 && showSelected) {
            setShowSelected(false);
        }
    }, [selectedCount, showSelected]);
    const categoryGroups = useMemo(() => {
        const lowerSearch = searchTerm.trim().toLowerCase();
        const groupMap = new Map();
        const order = [];
        features.forEach(feature => {
            const cat = feature.category ?? 'Other';
            if (!groupMap.has(cat)) {
                groupMap.set(cat, []);
                order.push(cat);
            }
            groupMap.get(cat)?.push(feature);
        });
        const groups = order.map(name => ({
            name,
            features: groupMap.get(name) ?? [],
        }));
        if (!lowerSearch && !showSelected)
            return groups;
        return groups
            .map(group => {
            const matchedFeatures = group.features.filter(feature => {
                if (showSelected && feature.value !== 'true') {
                    return false;
                }
                if (!lowerSearch) {
                    return true;
                }
                const label = feature.category === DATA_MODELLING_CATEGORY
                    ? t(DATA_MODELLING_FEATURE_TITLE_KEY)
                    : t(`${feature.configName}.label`) ||
                        feature.configName;
                return label.toLowerCase().includes(lowerSearch);
            });
            return matchedFeatures.length > 0
                ? { ...group, features: matchedFeatures }
                : null;
        })
            .filter(Boolean);
    }, [features, searchTerm, showSelected, t]);
    return (React.createElement(Vertical, { spacing: "d", grow: true, className: styles.featureStepWrapper, "data-testid": "step-feature-config" },
        !readOnly && (React.createElement(React.Fragment, null,
            React.createElement(Typography, { variant: "content-label-subhead" },
                t(OrgManagementTranslationKeys.WIZARD_GENERAL_ACCESS_FEATURES_TITLE),
                React.createElement(Typography, { variant: "p-small", overrideTag: "span", color: Colors.grayLight }, ` ${t(OrgManagementTranslationKeys.WIZARD_OPTIONAL)}`)),
            React.createElement("div", { className: styles.featureHeaderRow },
                React.createElement("div", { className: styles.featureSearchBarWrapper },
                    React.createElement(SearchBar, { searchValue: searchTerm, searchPlaceholder: t(OrgManagementTranslationKeys.SEARCH_PLACEHOLDER), onSearchChange: onSearchChange })),
                React.createElement("div", { className: styles.showSelectedWrapper },
                    React.createElement(Toggle, { checked: showSelected, disabled: selectedCount === 0, onChange: e => setShowSelected(e.target.checked), labelPosition: "right", label: t(OrgManagementTranslationKeys.WIZARD_SHOW_SELECTED), "data-testid": "show-selected-toggle" }))))),
        React.createElement("div", { className: styles.accordionScrollArea },
            React.createElement(LoadingIndicator.Global, { hideLoader: !loading, className: styles.featureLoader },
                React.createElement("div", { className: styles.accordionWrapper },
                    React.createElement(Accordion, { key: showSelected ? 'show-selected' : 'all', expandMultipleItems: true, variant: AccordionVariant.LONG_LIST, useUncontrolledState: !showSelected, expandedItemsIndexes: showSelected
                            ? categoryGroups.map((_, index) => index)
                            : undefined, className: styles.accordion }, categoryGroups.map(group => (React.createElement(Accordion.Item, { key: group.name, header: t(`${ConfigPrePostFixStrings.TSCLI_CONFIG}.${group.name}.${ConfigPrePostFixStrings.TITLE}`), "data-testid": `category-${group.name}` },
                        React.createElement("div", { className: styles.featureCardList }, group.features.map(feature => {
                            const label = feature.category ===
                                DATA_MODELLING_CATEGORY
                                ? t(DATA_MODELLING_FEATURE_TITLE_KEY)
                                : t(`${feature.configName}.label`) || feature.configName;
                            const infoTooltipKey = `${feature.configName}.infoTooltip`;
                            const infoTooltipValue = t(infoTooltipKey);
                            const description = infoTooltipValue !==
                                infoTooltipKey
                                ? infoTooltipValue
                                : undefined;
                            return (React.createElement(ConfigCard, { key: feature.configName, inputType: InputType.Checkbox, title: label, description: description, checked: feature.value === 'true', onChange: e => {
                                    const target = e.target;
                                    onFeatureValueChange(feature.configName, target.checked
                                        ? 'true'
                                        : 'false');
                                }, hide: readOnly, name: feature.configName, "data-testid": `feature-card-${feature.configName}` }));
                        })))))))))));
};
//# sourceMappingURL=step-feature-config.js.map